/**
 * Display label for a lab visit's B2B flow.
 *
 * Only flows whose handling differs from plain B2B are named — "B2B:BHT" and
 * "B2B:Kickstart" run distinct pipelines (fixed privat panel, own marker
 * panel), so anywhere a visit is shown it should say which one it is.
 * healthCheck and consumer are plain B2B and return null; callers that
 * already know the visit is B2B fall back to "B2B" themselves.
 */
export function b2bFlowLabel(
  flowType: string | null | undefined,
): string | null {
  switch (flowType) {
    case "drDropinBht":
      return "B2B:BHT";
    case "kickstart":
      return "B2B:Kickstart";
    default:
      return null;
  }
}

/**
 * What kind of visit this is, for every visit — not just the named B2B flows.
 * `b2bFlowType` alone is too sparse to label with: plenty of B2B visits carry
 * only `origin: "b2b"` + a companyId (nothing sets the flow type unless the
 * visit belongs to one of the special pipelines), and B2C visits have no flow
 * type at all. Falling through those to "no badge" hides the answer to the
 * question the badge exists to answer.
 *
 * Precedence: named B2B pipeline → self-reported → plain B2B → origin.
 */
export function visitFlowLabel(visit: {
  b2bFlowType?: string | null;
  origin?: string | null;
  companyId?: string | null;
  selfReported?: boolean | null;
}): string {
  switch (visit.b2bFlowType) {
    case "drDropinBht":
      return "B2B:BHT";
    case "kickstart":
      return "B2B:Kickstart";
    case "healthCheck":
      return "B2B:Helsesjekk";
    case "consumer":
      return "B2B:Forbruker";
  }

  // Patient-entered results — no requisition was ever sent for these.
  if (visit.selfReported || visit.origin === "selfReport")
    return "Selvrapportert";

  // B2B without a flow type: the common case, and the one Astrid-style seed
  // visits land in. companyId is the tell when origin wasn't set either.
  if (visit.origin === "b2b" || visit.companyId) return "B2B";

  switch (visit.origin) {
    case "subscription":
      return "Abonnement";
    case "singlePurchase":
      return "Enkeltkjøp";
    case "admin":
      return "Admin";
    case "furstWebhook":
    case "furstUpload":
    case "directImport":
      return "Import";
    default:
      return "Privat";
  }
}
